import React, { FC, useEffect, useState } from "react"
import { KButton, KDropdown, KInput, KSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { SetURLSearchParams } from "react-router-dom"
import { toast } from "react-toastify"

import Loading from "../../../../ui/Loading"
import Pagination from "../../../../ui/table/Pagination"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"
import { assistant } from "../../../../../apis"
import { errorFilledToastOptions } from "../../../../../constants"
import {
  CoManagerLogConversation,
  CoManagerLogDetail,
  CoManagerLogPeriod,
  CoManagerLogSummary
} from "../../../../../types"
import { mapListToCamelCase, mapToCamelCase } from "../../../../../utility"

const LIMIT = 20

// What each action a reply rendered is called on this screen. Mirrors ACTIONS_BY_TOOL on the server.
const ACTION_LABELS: { [action: string]: string } = {
  new_release: "New release",
  release_plan: "Release plan card",
  promote_track: "Rising track smart link",
  smart_link: "Smart link picker",
  royalties: "Royalties page",
  payment_method: "Payment methods",
  songtools: "Songtools",
  infringement: "Infringement catalog",
  rights_documents: "Rights document request",
  rights_dispute: "Rights dispute request",
  claim_support: "Content ID request",
  generate_cover: "Cover generation",
  download_report: "Royalty report"
}

// The data tools the model can read with. Mirrors DATA_TOOL_SCHEMAS on the server.
const TOOL_LABELS: { [tool: string]: string } = {
  get_daily_streams: "Daily streams",
  get_listener_countries: "Listener countries",
  get_playlist_placements: "Playlists",
  get_statement_detail: "Statement",
  get_spotify_pitching: "Spotify pitching"
}

const ALL_OPTION: KSelectOption = { label: "All", value: 0, value2: "" }
const actionOptions: KSelectOption[] = [
  ALL_OPTION,
  ...Object.entries(ACTION_LABELS).map(([action, label], index) => ({ label, value: index + 1, value2: action }))
]
const toolOptions: KSelectOption[] = [
  ALL_OPTION,
  ...Object.entries(TOOL_LABELS).map(([tool, label], index) => ({ label, value: index + 1, value2: tool }))
]

const columnWidths: { [column: string]: string } = {
  ID: "5%",
  USER: "18%",
  TITLE: "25%",
  MESSAGES: "7%",
  ACTIONS: "17%",
  TOOLS: "12%",
  COST: "7%",
  LAST_ACTIVITY: "9%"
}

// Model calls cost fractions of a cent, so two decimals would read as $0.00 on nearly every row.
const formatUsd = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : `$${value.toFixed(4)}`

const formatDate = (value: string | undefined) => (value ? value.substring(0, 19).replace("T", " ") : "-")

const userLabel = (user: CoManagerLogConversation["user"]) => user.name || user.username

const Chip: FC<{ text: string; color: string; background: string }> = ({ text, color, background }) => (
  <div className="px-2 py-0.5 rounded-md" style={{ background }}>
    <KSpan text={text} fontSize={11} lineHeight="16px" color={color} />
  </div>
)

type AdminCoManagerLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
}

/**
 * Every artist's co-manager conversations, read only: what they asked, what it answered, which button
 * or card a reply put under itself, what it looked up, and what the model calls cost. Opening one is
 * written to the user logs on the server, the conversation is the artist's private chat.
 */
const AdminCoManagerLog: FC<AdminCoManagerLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [conversations, setConversations] = useState<CoManagerLogConversation[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [summary, setSummary] = useState<CoManagerLogSummary | undefined>(undefined)
  const [detail, setDetail] = useState<CoManagerLogDetail | undefined>(undefined)

  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>(undefined)
  const [selectedAction, setSelectedAction] = useState<KSelectOption>(ALL_OPTION)
  const [selectedTool, setSelectedTool] = useState<KSelectOption>(ALL_OPTION)
  const [search, setSearch] = useState("")

  const fetchConversations = (currentOffset: number) => {
    const query = new URLSearchParams({ limit: String(LIMIT), offset: String(currentOffset) })
    if (selectedUser) query.set("user_id", String(selectedUser.value))
    if (selectedAction.value2) query.set("action", String(selectedAction.value2))
    if (selectedTool.value2) query.set("tool", String(selectedTool.value2))
    if (search.trim()) query.set("search", search.trim())

    setLoading(true)
    assistant(`/co-manager-logs/?${query.toString()}`, "get", null, {}, false)
      .then((response) => {
        setConversations(mapListToCamelCase<CoManagerLogConversation>(response.data.results))
        setTotalCount(response.data.count || 0)
      })
      .catch(() => toast.error("Co-manager logs could not be loaded", errorFilledToastOptions))
      .finally(() => setLoading(false))
  }

  const openConversation = (id: number) => {
    setLoading(true)
    assistant(`/co-manager-logs/${id}/`, "get", null, {}, false)
      .then((response) => setDetail(mapToCamelCase<CoManagerLogDetail>(response.data)))
      .catch(() => toast.error("The conversation could not be opened", errorFilledToastOptions))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    assistant("/co-manager-logs/summary/", "get", null, {}, false)
      .then((response) => setSummary(mapToCamelCase<CoManagerLogSummary>(response.data)))
      .catch(() => setSummary(undefined))
  }, [])

  // The user picker applies on its own, like on the other log screens; the rest wait for Apply.
  useEffect(() => {
    setOffset(0)
    fetchConversations(0)
  }, [selectedUser])

  const renderPeriod = (title: string, period: CoManagerLogPeriod | undefined) => (
    <div className="flex flex-col gap-1 rounded-[10px] p-3 grow" style={{ border: "1px solid #E5E7EB" }}>
      <KSpan text={title} fontSize={12} lineHeight="16px" color="#6B7280" />
      <KSpan text={formatUsd(period?.costUsd)} fontSize={20} lineHeight="28px" fontWeight={600} color="#111" />
      <KSpan
        text={
          period
            ? `${period.replies} replies · ${period.calls} model calls · ${period.users} ${
                period.users === 1 ? "user" : "users"
              }${period.failedCalls ? ` · ${period.failedCalls} failed` : ""}`
            : "-"
        }
        fontSize={12}
        lineHeight="16px"
        color={period?.failedCalls ? "#B91C1C" : "#6B7280"}
      />
    </div>
  )

  const renderFilters = () => (
    <div className="flex gap-3 items-center flex-wrap justify-end">
      <div className="flex items-center gap-2">
        <KSpan text="User" />
        <SearchUserComponent className="relative" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
      </div>
      <KDropdown
        label="Action"
        options={actionOptions}
        selected={selectedAction}
        onSelect={(selected) => setSelectedAction((selected as KSelectOption) || ALL_OPTION)}
        width={190}
        height={35}
      />
      <KDropdown
        label="Tool"
        options={toolOptions}
        selected={selectedTool}
        onSelect={(selected) => setSelectedTool((selected as KSelectOption) || ALL_OPTION)}
        width={160}
        height={35}
      />
      <KInput
        value={search}
        onChange={setSearch}
        placeholder="Search in messages"
        width={200}
        height={35}
        leftIcon="/catalog_icons/search-grey.svg"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            setOffset(0)
            fetchConversations(0)
          }
        }}
      />
      <KButton
        height="35px"
        width="80px"
        text="Apply"
        background="black"
        textColor="white"
        onClick={() => {
          setOffset(0)
          fetchConversations(0)
        }}
      />
    </div>
  )

  const renderList = () => {
    if (!conversations.length && !loading) {
      return (
        <div className="flex justify-center items-center py-20">
          <KSpan text="No co-manager conversations found" color="#999" />
        </div>
      )
    }

    return (
      <div className="pt-4 pb-4 flex flex-col">
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => (
            <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
              <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
            </div>
          ))}
        </div>
        {conversations.map((conversation) => (
          <div
            key={`conversation_${conversation.id}`}
            className="flex py-3 gap-2 cursor-pointer hover:bg-[#F9FAFB]"
            style={{ width: "100%", borderTop: "1px dashed grey" }}
            onClick={() => openConversation(conversation.id)}
          >
            <div style={{ width: columnWidths.ID }} className="flex items-center">
              <KSpan text={String(conversation.id)} />
            </div>
            <div style={{ width: columnWidths.USER }} className="flex flex-col justify-center overflow-hidden">
              <KSpan text={userLabel(conversation.user)} color="#111" />
              <KSpan text={conversation.user.email} fontSize={11} color="#6B7280" />
            </div>
            <div style={{ width: columnWidths.TITLE }} className="flex items-center gap-2 overflow-hidden">
              <KSpan text={conversation.title || "-"} color="#111" />
              {conversation.archived && <Chip text="Archived" color="#6B7280" background="#F3F4F6" />}
            </div>
            <div style={{ width: columnWidths.MESSAGES }} className="flex items-center">
              <KSpan text={String(conversation.messageCount)} />
            </div>
            <div style={{ width: columnWidths.ACTIONS }} className="flex items-center gap-1 flex-wrap">
              {conversation.actions.length ? (
                conversation.actions.map((action) => (
                  <Chip key={action} text={ACTION_LABELS[action] || action} color="#166534" background="#F0FDF4" />
                ))
              ) : (
                <KSpan text="-" />
              )}
            </div>
            <div style={{ width: columnWidths.TOOLS }} className="flex items-center gap-1 flex-wrap">
              {conversation.toolsUsed.length ? (
                conversation.toolsUsed.map((tool) => (
                  <Chip key={tool} text={TOOL_LABELS[tool] || tool} color="#1D4ED8" background="#EFF6FF" />
                ))
              ) : (
                <KSpan text="-" />
              )}
            </div>
            <div style={{ width: columnWidths.COST }} className="flex items-center">
              <KSpan text={formatUsd(conversation.costUsd)} />
            </div>
            <div style={{ width: columnWidths.LAST_ACTIVITY }} className="flex items-center">
              <KSpan text={formatDate(conversation.updated)} fontSize={12} />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const renderDetail = (conversation: CoManagerLogDetail) => (
    <div className="flex flex-col gap-4 pb-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <KButton
            text="Back"
            leftIcon="/navigation_icons/arrow-left-line.svg"
            onClick={() => setDetail(undefined)}
            background="transparent"
            shadowDisabled
            width="auto"
            height="32px"
            padding="0px 8px"
            fontSize={13}
            textColor="#111"
          />
          <div className="flex flex-col">
            <KSpan text={conversation.title || "-"} fontSize={16} lineHeight="24px" fontWeight={600} color="#111" />
            <KSpan
              text={`${userLabel(conversation.user)} · ${conversation.user.email} · started ${formatDate(
                conversation.created
              )}`}
              fontSize={12}
              color="#6B7280"
            />
          </div>
          {conversation.archived && <Chip text="Archived by the artist" color="#6B7280" background="#F3F4F6" />}
        </div>
        <KSpan text={`Total cost ${formatUsd(conversation.costUsd)}`} fontWeight={500} color="#111" />
      </div>

      <div className="flex flex-col gap-3 rounded-[10px] p-4" style={{ background: "#F9FAFB" }}>
        {conversation.messages.map((message) => {
          const fromArtist = message.role === "user"
          const meta = message.meta || {}
          return (
            <div
              key={`message_${message.id}`}
              className={`flex flex-col gap-1 ${fromArtist ? "items-end" : "items-start"}`}
            >
              <div
                className="rounded-[10px] px-3 py-2 max-w-[75%] whitespace-pre-wrap"
                style={{ background: fromArtist ? "#111" : "#FFF", border: fromArtist ? "none" : "1px solid #E5E7EB" }}
              >
                <KSpan text={message.content} fontSize={13} lineHeight="20px" color={fromArtist ? "#FFF" : "#111"} />
              </div>
              <div className="flex items-center gap-1 flex-wrap">
                <KSpan text={formatDate(message.created)} fontSize={11} color="#9CA3AF" />
                {meta.imageCount ? (
                  <Chip text={`${meta.imageCount} image(s)`} color="#6B7280" background="#F3F4F6" />
                ) : null}
                {meta.action && (
                  <Chip
                    text={[
                      ACTION_LABELS[meta.action] || meta.action,
                      meta.reportPeriod,
                      meta.releaseId ? `#${meta.releaseId}` : undefined,
                      meta.planTitles?.length ? `(${meta.planTitles.join(", ")})` : undefined
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    color="#166534"
                    background="#F0FDF4"
                  />
                )}
                {(meta.toolsUsed || []).map((tool) => (
                  <Chip key={tool} text={TOOL_LABELS[tool] || tool} color="#1D4ED8" background="#EFF6FF" />
                ))}
                {meta.promptVersion && (
                  <Chip text={`prompt ${meta.promptVersion}`} color="#6B7280" background="#F3F4F6" />
                )}
                {meta.locale && <Chip text={meta.locale} color="#6B7280" background="#F3F4F6" />}
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex flex-col">
        <KSpan text="Model calls" fontWeight={600} color="#111" />
        <div className="flex pt-3 pb-2 gap-2">
          {["Time", "Kind", "Model", "Input", "Cached", "Output", "Duration", "Cost", "Status"].map((header) => (
            <div key={`usage_header_${header}`} className="w-[11%]">
              <KSpan text={header} fontWeight={500} />
            </div>
          ))}
        </div>
        {conversation.usages.map((usage) => (
          <div key={`usage_${usage.id}`} className="flex py-2 gap-2" style={{ borderTop: "1px dashed grey" }}>
            <div className="w-[11%]">
              <KSpan text={formatDate(usage.created)} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={usage.kind} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={usage.model} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={usage.promptTokens.toLocaleString("en-US")} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={usage.cachedTokens.toLocaleString("en-US")} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={usage.completionTokens.toLocaleString("en-US")} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={`${(usage.durationMs / 1000).toFixed(1)} s`} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan text={formatUsd(usage.costUsd)} fontSize={12} />
            </div>
            <div className="w-[11%]">
              <KSpan
                text={usage.success ? "OK" : "Failed"}
                fontSize={12}
                color={usage.success ? "#166534" : "#B91C1C"}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4 grow overflow-x-auto">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6 gap-4">
        {props.headerDiv}
        {!detail && renderFilters()}
      </div>
      {detail ? (
        renderDetail(detail)
      ) : (
        <>
          <div className="flex gap-3">
            {renderPeriod("Today", summary?.today)}
            {renderPeriod("Last 7 days", summary?.last7Days)}
            {renderPeriod("Last 30 days", summary?.last30Days)}
          </div>
          {renderList()}
          <Pagination
            hidden={totalCount <= LIMIT}
            pageCount={Math.ceil(totalCount / LIMIT)}
            currentPage={Math.floor(offset / LIMIT) + 1}
            onClickPage={(page: number) => {
              const newOffset = (page - 1) * LIMIT
              setOffset(newOffset)
              fetchConversations(newOffset)
            }}
          />
        </>
      )}
    </div>
  )
}

export default AdminCoManagerLog
